import {
    ArrowPathIcon,
    BanknotesIcon,
    CalculatorIcon,
    ArrowsRightLeftIcon,
    CheckIcon,
    ClipboardDocumentCheckIcon,
    Cog6ToothIcon,
    DocumentTextIcon,
    EnvelopeIcon,
    ExclamationTriangleIcon,
    GiftIcon,
    LinkIcon,
    LinkSlashIcon,
    NoSymbolIcon,
    PencilSquareIcon,
    PlusIcon,
    QuestionMarkCircleIcon,
    TruckIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import type { ChargeEventItem } from '../../services/billing';

// eventType alone is ambiguous - charge created, invoice created and discount
// issued are all 'created'; naming needs payload.action. Split out for react-refresh.

interface EventTone {
    ring: string;
    icon: string;
}

const TONE_NEUTRAL: EventTone = { ring: 'border-gray-300 bg-white', icon: 'text-gray-600' };
const TONE_CREATED: EventTone = { ring: 'border-yellow-300 bg-yellow-50', icon: 'text-yellow-700' };
const TONE_DOCUMENT: EventTone = { ring: 'border-blue-300 bg-blue-50', icon: 'text-blue-700' };
const TONE_SUCCESS: EventTone = {
    ring: 'border-emerald-300 bg-emerald-50',
    icon: 'text-emerald-700'
};
const TONE_FAILURE: EventTone = { ring: 'border-red-300 bg-red-50', icon: 'text-red-700' };
// Amber, so a type this map has not caught up with looks wrong rather than
// blending in as a normal entry.
const TONE_UNKNOWN: EventTone = { ring: 'border-amber-300 bg-amber-50', icon: 'text-amber-700' };
const TONE_DISCOUNT: EventTone = {
    ring: 'border-purple-300 bg-purple-50',
    icon: 'text-purple-700'
};

export interface BillingEventStyle {
    icon: typeof PlusIcon;
    tone: EventTone;
    label: string;
}

// Falls back to a generic noun so an entity added to the backend enum first
// reads as "Record created" rather than showing an admin a raw identifier.
const ENTITY_NOUN: Record<string, string> = {
    charge: 'Charge',
    invoice: 'Invoice',
    purchase_order: 'Purchase order'
};

function entityNoun(entityType: string): string {
    return ENTITY_NOUN[entityType] ?? 'Record';
}

// manual_trigger is a catch-all; payload.action says which action actually ran,
// turning a run of identical "Manual action" rows into readable history.
const ACTION_STYLES: Record<string, BillingEventStyle> = {
    one_time_discount_linked: {
        icon: LinkIcon,
        tone: TONE_DISCOUNT,
        label: 'One-time discount linked'
    },
    one_time_discount_unlinked: {
        icon: LinkSlashIcon,
        tone: TONE_DISCOUNT,
        label: 'One-time discount unlinked'
    },
    one_time_discount_created_on_void: {
        icon: GiftIcon,
        tone: TONE_DISCOUNT,
        label: 'One-time discount issued on void'
    },
    charge_updated: { icon: PencilSquareIcon, tone: TONE_NEUTRAL, label: 'Charge updated' },
    processing_started: { icon: Cog6ToothIcon, tone: TONE_NEUTRAL, label: 'Processing started' }
};

/** Label built from the entity noun, so "created" reads as "Charge created". */
function baseStyle(eventType: string, entityType: string): BillingEventStyle {
    const noun = entityNoun(entityType);
    switch (eventType) {
        case 'created':
            return { icon: PlusIcon, tone: TONE_CREATED, label: `${noun} created` };
        case 'claimed':
            return {
                icon: Cog6ToothIcon,
                tone: TONE_NEUTRAL,
                label: `${noun} claimed for processing`
            };
        case 'invoice_created':
            return { icon: DocumentTextIcon, tone: TONE_DOCUMENT, label: 'Invoice created' };
        case 'po_created':
            return { icon: DocumentTextIcon, tone: TONE_NEUTRAL, label: 'Purchase order created' };
        case 'status_synced':
            return {
                icon: ArrowsRightLeftIcon,
                tone: TONE_NEUTRAL,
                label: `${noun} status synced`
            };
        case 'document_stored':
            return { icon: DocumentTextIcon, tone: TONE_NEUTRAL, label: 'Document stored' };
        case 'email_sent':
            return { icon: EnvelopeIcon, tone: TONE_NEUTRAL, label: `${noun} emailed` };
        case 'retried':
            return { icon: ArrowPathIcon, tone: TONE_NEUTRAL, label: `${noun} retried` };
        case 'voided':
            return { icon: XMarkIcon, tone: TONE_FAILURE, label: `${noun} voided` };
        case 'canceled':
            return { icon: NoSymbolIcon, tone: TONE_FAILURE, label: `${noun} canceled` };
        case 'charge_invoiced':
            return { icon: CheckIcon, tone: TONE_SUCCESS, label: 'Charge invoiced' };
        case 'manual_trigger':
            return { icon: Cog6ToothIcon, tone: TONE_NEUTRAL, label: 'Manual action' };
        case 'po_placed':
            return {
                icon: ClipboardDocumentCheckIcon,
                tone: TONE_SUCCESS,
                label: 'Purchase order placed on vendor site'
            };
        case 'po_shipped':
            return { icon: TruckIcon, tone: TONE_DOCUMENT, label: 'Purchase order shipped' };
        case 'po_received':
            return { icon: CheckIcon, tone: TONE_SUCCESS, label: 'Purchase order received' };
        case 'po_line_updated':
            return {
                icon: PencilSquareIcon,
                tone: TONE_NEUTRAL,
                label: 'Purchase order line updated'
            };
        case 'po_billed':
            return {
                icon: DocumentTextIcon,
                tone: TONE_DOCUMENT,
                label: 'Vendor invoice recorded'
            };
        case 'po_unbilled':
            return {
                icon: ArrowPathIcon,
                tone: TONE_NEUTRAL,
                label: 'Vendor invoice removed'
            };
        case 'po_paid':
            return { icon: BanknotesIcon, tone: TONE_SUCCESS, label: 'Purchase order paid' };
        case 'po_disputed':
            return {
                icon: ExclamationTriangleIcon,
                tone: TONE_FAILURE,
                label: 'Vendor invoice disputed'
            };
        case 'po_canceled':
            return { icon: NoSymbolIcon, tone: TONE_FAILURE, label: 'Purchase order canceled' };
        case 'expected_cost_imputed':
            return {
                icon: CalculatorIcon,
                tone: TONE_NEUTRAL,
                label: 'Expected cost imputed'
            };
        case 'error':
            return {
                icon: ExclamationTriangleIcon,
                tone: TONE_FAILURE,
                label: 'Processing failed'
            };
        default:
            // Named as unhandled, not shown as a plausible label - the same
            // treatment poStatusDisplay gives an unmapped status.
            return {
                icon: QuestionMarkCircleIcon,
                tone: TONE_UNKNOWN,
                label: `Unknown event (${eventType})`
            };
    }
}

function payloadRecord(payload: unknown): Record<string, unknown> | null {
    if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) return null;
    return payload as Record<string, unknown>;
}

export function billingEventStyle(event: ChargeEventItem): BillingEventStyle {
    const payload = payloadRecord(event.payload);

    // Most specific first: an explicit action names itself.
    const action = typeof payload?.action === 'string' ? payload.action : null;
    if (action && ACTION_STYLES[action]) {
        return ACTION_STYLES[action];
    }

    // A one-time discount arrives as a 'created' event on its charge (there is
    // no one_time_discount entity type), so it looks like the charge's own.
    if (event.eventType === 'created' && payload?.oneTimeDiscountId !== undefined) {
        return { icon: GiftIcon, tone: TONE_DISCOUNT, label: 'One-time discount created' };
    }

    return baseStyle(event.eventType, event.entityType);
}
